import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users, Calendar, HandHelping, Heart, Compass, ArrowLeft, CheckCircle2, Loader2,
  AlertCircle, ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAVY = "#0B192C";
const GOLD = "#F5A623";
const CREAM = "#FDFBF7";
const SLATE = "#475569";

const FREQUENCY_OPTIONS = ["Weekly", "Bi-Weekly", "Monthly", "Occasionally", "As Needed"];
const DAY_OPTIONS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_OPTIONS = ["Morning", "Afternoon", "Evening"];
const INTEREST_OPTIONS = [
  "Community Outreach",
  "Event Support",
  "Applicant Assistance",
  "Food & Supply Distribution",
  "Resource Navigation",
  "Social Media / Marketing",
  "Photography / Video",
  "Fundraising Support",
  "Business Outreach",
  "Transportation Assistance",
  "Warehouse / Inventory Support",
  "Youth & Family Support"
];

const initialState = {
  full_name: "",
  phone: "",
  email: "",
  city: "",
  contact_preference: "email",
  frequency: [],
  days_available: [],
  time_availability: [],
  areas_of_interest: [],
  other_interest: "",
  skills: "",
  why_volunteer: "",
  additional_info: "",
  agreed_to_terms: false
};

const WAYS_TO_HELP = [
  {
    icon: Users,
    title: "Community Outreach",
    desc: "Help us connect with families, neighborhoods, businesses, and community partners."
  },
  {
    icon: Calendar,
    title: "Event Support",
    desc: "Assist with local gatherings, resource days, donation drives, and community events."
  },
  {
    icon: HandHelping,
    title: "Applicant Support",
    desc: "Help guide people through the application process with care, dignity, and respect."
  },
  {
    icon: Heart,
    title: "Follow-Up Support",
    desc: "Check in with families after assistance has been provided and help connect them to additional resources."
  },
  {
    icon: Compass,
    title: "Resource Navigation",
    desc: "Help identify local resources for food, utilities, housing, transportation, counseling, and emergency support."
  }
];

const sectionCard = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "16px",
  padding: "28px 32px",
  marginBottom: "24px"
};

const labelStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: "600",
  color: NAVY,
  marginBottom: "8px",
  letterSpacing: "0.02em"
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  border: "1px solid #CBD5E1",
  borderRadius: "10px",
  fontSize: "15px",
  color: NAVY,
  backgroundColor: "#FFFFFF",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box"
};

const textareaStyle = { ...inputStyle, minHeight: "110px", resize: "vertical", lineHeight: "1.5" };

const checkRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px 12px",
  borderRadius: "8px",
  cursor: "pointer",
  border: "1px solid #E2E8F0",
  backgroundColor: "#FFFFFF",
  fontSize: "14px",
  color: NAVY,
  userSelect: "none",
  transition: "all 0.15s ease"
};

const checkboxSelected = {
  borderColor: GOLD,
  backgroundColor: "rgba(245, 166, 35, 0.08)",
  color: NAVY,
  fontWeight: "600"
};

const sectionHeading = {
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: "1.5rem",
  fontWeight: "500",
  color: NAVY,
  margin: "0 0 6px"
};

const sectionSubhead = {
  fontSize: "14px",
  color: SLATE,
  margin: "0 0 18px"
};

const VolunteerPage = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const toggleArray = (key, value) =>
    setForm((p) => ({
      ...p,
      [key]: p[key].includes(value) ? p[key].filter((v) => v !== value) : [...p[key], value]
    }));

  const scrollToForm = () => {
    document.getElementById("vol-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.agreed_to_terms) {
      setError("Please agree to the volunteer expectations to submit your application.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteers`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong submitting your application. Please try again.";
      setError(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: CREAM, fontFamily: "'Outfit', sans-serif" }}>
        <SimpleHeader />
        <div style={{ maxWidth: "720px", margin: "0 auto", padding: "120px 24px", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "84px",
              height: "84px",
              borderRadius: "50%",
              backgroundColor: "rgba(245, 166, 35, 0.15)",
              marginBottom: "28px"
            }}
          >
            <CheckCircle2 size={44} style={{ color: GOLD }} />
          </div>
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 4vw, 2.75rem)",
              color: NAVY,
              fontWeight: "500",
              marginBottom: "20px"
            }}
          >
            Thank You for Stepping Up
          </h1>
          <p style={{ fontSize: "17px", color: SLATE, lineHeight: "1.7", marginBottom: "16px" }}>
            Thank you for your interest in volunteering with The Rich Porch. A member of our team will
            review your application and contact you soon.
          </p>
          <p style={{ fontSize: "17px", color: SLATE, lineHeight: "1.7", marginBottom: "36px" }}>
            Together, we can make a real difference in our community.
          </p>
          <Link
            to="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "14px 28px",
              borderRadius: "50px",
              backgroundColor: NAVY,
              color: "#FFFFFF",
              textDecoration: "none",
              fontWeight: "600",
              fontSize: "14px",
              letterSpacing: "0.04em"
            }}
          >
            <ArrowLeft size={16} /> RETURN HOME
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: CREAM, fontFamily: "'Outfit', sans-serif" }}>
      <SimpleHeader />

      {/* Hero / Page Header */}
      <section style={{ backgroundColor: NAVY, padding: "80px 24px 90px", color: "#FFFFFF" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontSize: "13px",
              letterSpacing: "0.3em",
              color: GOLD,
              fontWeight: "600",
              marginBottom: "16px",
              textTransform: "uppercase"
            }}
          >
            Volunteer with The Rich Porch
          </p>
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2.25rem, 5vw, 3.5rem)",
              fontWeight: "500",
              lineHeight: "1.15",
              margin: "0 0 24px"
            }}
          >
            Get Involved
          </h1>
          <p style={{ fontSize: "17px", lineHeight: "1.75", color: "#CBD5E1", maxWidth: "720px", margin: "0 auto 32px" }}>
            We are looking for compassionate, dependable volunteers who want to help families in our
            community during moments of real need. Whether you can give a few hours, help with outreach,
            support local events, or assist behind the scenes —{" "}
            <span style={{ color: GOLD, fontWeight: "600" }}>your involvement matters.</span>
          </p>
          <button
            onClick={scrollToForm}
            style={{
              padding: "14px 32px",
              backgroundColor: GOLD,
              color: NAVY,
              fontWeight: "700",
              fontSize: "14px",
              letterSpacing: "0.05em",
              borderRadius: "50px",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            APPLY TO VOLUNTEER <ChevronRight size={18} />
          </button>
        </div>
      </section>

      {/* Ways You Can Help */}
      <section style={{ padding: "80px 24px", backgroundColor: "#FFFFFF" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 4vw, 2.75rem)",
              fontWeight: "500",
              color: NAVY,
              textAlign: "center",
              margin: "0 0 12px"
            }}
          >
            Ways You Can Help
          </h2>
          <div
            style={{
              width: "60px",
              height: "3px",
              backgroundColor: GOLD,
              margin: "0 auto 48px"
            }}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
              gap: "20px"
            }}
          >
            {WAYS_TO_HELP.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                style={{
                  backgroundColor: CREAM,
                  border: "1px solid #E2E8F0",
                  borderRadius: "16px",
                  padding: "26px 24px",
                  transition: "all 0.2s ease"
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "12px",
                    backgroundColor: "rgba(245, 166, 35, 0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px"
                  }}
                >
                  <Icon size={24} style={{ color: GOLD }} />
                </div>
                <h3
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "1.35rem",
                    fontWeight: "500",
                    color: NAVY,
                    margin: "0 0 8px"
                  }}
                >
                  {title}
                </h3>
                <p style={{ color: SLATE, lineHeight: "1.65", fontSize: "14px", margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section style={{ padding: "70px 24px", backgroundColor: CREAM }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 4vw, 2.5rem)",
              fontWeight: "500",
              color: NAVY,
              margin: "0 0 20px"
            }}
          >
            Who We're Looking For
          </h2>
          <p style={{ fontSize: "16px", color: SLATE, lineHeight: "1.75", marginBottom: "28px" }}>
            We welcome people who are:
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "32px"
            }}
          >
            {["Dependable", "Compassionate", "Respectful", "Community-minded", "Able to protect dignity & privacy"].map(
              (trait) => (
                <span
                  key={trait}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#FFFFFF",
                    border: `1.5px solid ${GOLD}`,
                    color: NAVY,
                    borderRadius: "50px",
                    fontSize: "14px",
                    fontWeight: "500"
                  }}
                >
                  {trait}
                </span>
              )
            )}
          </div>
          <p
            style={{
              fontSize: "17px",
              color: NAVY,
              lineHeight: "1.7",
              fontStyle: "italic",
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: "500",
              maxWidth: "640px",
              margin: "0 auto"
            }}
          >
            You do not need to have a special title or background to make a difference. You simply need
            a heart to help and a willingness to serve.
          </p>
        </div>
      </section>

      {/* Why Volunteer */}
      <section style={{ padding: "70px 24px", backgroundColor: "#FFFFFF" }}>
        <div style={{ maxWidth: "780px", margin: "0 auto", textAlign: "center" }}>
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(1.85rem, 3.5vw, 2.5rem)",
              fontWeight: "500",
              color: NAVY,
              margin: "0 0 20px"
            }}
          >
            Why Volunteer With Us?
          </h2>
          <p style={{ fontSize: "17px", color: SLATE, lineHeight: "1.8", margin: 0 }}>
            Because families are facing real emergencies right now. Your time can help turn a moment of
            crisis into a moment of hope.
          </p>
        </div>
      </section>

      {/* Ready to Get Involved CTA */}
      <section style={{ padding: "70px 24px", backgroundColor: NAVY, color: "#FFFFFF", textAlign: "center" }}>
        <div style={{ maxWidth: "780px", margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(1.85rem, 3.5vw, 2.5rem)",
              fontWeight: "500",
              margin: "0 0 20px"
            }}
          >
            Ready to Get Involved?
          </h2>
          <p style={{ fontSize: "17px", color: "#CBD5E1", lineHeight: "1.75", margin: 0 }}>
            Join us as we build a stronger, more responsive community —{" "}
            <span style={{ color: GOLD, fontWeight: "600" }}>
              one neighbor, one family, and one act of help at a time.
            </span>
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section id="vol-form" style={{ padding: "80px 24px", backgroundColor: CREAM }}>
        <div style={{ maxWidth: "820px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <h2
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(2rem, 4vw, 2.75rem)",
                fontWeight: "500",
                color: NAVY,
                margin: "0 0 8px"
              }}
            >
              Volunteer Application Form
            </h2>
            <p style={{ color: SLATE, fontSize: "15px" }}>
              Tell us a little about you and how you'd like to help.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div style={sectionCard}>
              <h3 style={sectionHeading}>Basic Information</h3>
              <p style={sectionSubhead}>How can we reach you?</p>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: "16px" }}>
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email Address *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>City / Neighborhood *</label>
                  <input
                    type="text"
                    required
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Preferred Method of Contact *</label>
                  <select
                    required
                    value={form.contact_preference}
                    onChange={(e) => update("contact_preference", e.target.value)}
                    style={inputStyle}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="text">Text</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Availability */}
            <div style={sectionCard}>
              <h3 style={sectionHeading}>Availability</h3>
              <p style={sectionSubhead}>When can you serve?</p>

              <label style={labelStyle}>How often would you like to volunteer?</label>
              <CheckGroup options={FREQUENCY_OPTIONS} values={form.frequency} onToggle={(v) => toggleArray("frequency", v)} />

              <div style={{ height: 20 }} />
              <label style={labelStyle}>What days are you generally available?</label>
              <CheckGroup options={DAY_OPTIONS} values={form.days_available} onToggle={(v) => toggleArray("days_available", v)} />

              <div style={{ height: 20 }} />
              <label style={labelStyle}>Preferred Time Availability</label>
              <CheckGroup options={TIME_OPTIONS} values={form.time_availability} onToggle={(v) => toggleArray("time_availability", v)} />
            </div>

            {/* Areas of Interest */}
            <div style={sectionCard}>
              <h3 style={sectionHeading}>Areas of Interest</h3>
              <p style={sectionSubhead}>Which areas would you like to help with?</p>

              <CheckGroup options={INTEREST_OPTIONS} values={form.areas_of_interest} onToggle={(v) => toggleArray("areas_of_interest", v)} />

              <div style={{ marginTop: 16 }}>
                <label style={labelStyle}>Other (please specify)</label>
                <input
                  type="text"
                  value={form.other_interest}
                  onChange={(e) => update("other_interest", e.target.value)}
                  style={inputStyle}
                  placeholder="Something not listed above…"
                />
              </div>
            </div>

            {/* Skills & Experience */}
            <div style={sectionCard}>
              <h3 style={sectionHeading}>Skills & Experience</h3>
              <p style={sectionSubhead}>
                Do you have any skills, experience, or certifications that may help us serve the community?
              </p>
              <textarea
                value={form.skills}
                onChange={(e) => update("skills", e.target.value)}
                style={textareaStyle}
                placeholder="Examples: medical, counseling, case management, security, event coordination, social work, graphic design, technology, translation services, CDL / transportation, nonprofit experience…"
              />
            </div>

            {/* Additional Information */}
            <div style={sectionCard}>
              <h3 style={sectionHeading}>Additional Information</h3>
              <p style={sectionSubhead}>Help us get to know you a little better.</p>

              <label style={labelStyle}>Why would you like to volunteer with The Rich Porch?</label>
              <textarea
                value={form.why_volunteer}
                onChange={(e) => update("why_volunteer", e.target.value)}
                style={textareaStyle}
              />

              <div style={{ height: 16 }} />
              <label style={labelStyle}>Is there anything else you would like us to know?</label>
              <textarea
                value={form.additional_info}
                onChange={(e) => update("additional_info", e.target.value)}
                style={textareaStyle}
              />
            </div>

            {/* Volunteer Expectations */}
            <div style={{ ...sectionCard, backgroundColor: NAVY, border: "none" }}>
              <h3 style={{ ...sectionHeading, color: "#FFFFFF" }}>Volunteer Expectations</h3>
              <p style={{ color: "#CBD5E1", fontSize: "14px", marginBottom: "16px" }}>
                By submitting this form, I understand that:
              </p>
              <ul style={{ paddingLeft: "20px", margin: "0 0 20px", color: "#CBD5E1", lineHeight: "1.8", fontSize: "14px" }}>
                <li>The Rich Porch is a community-focused initiative built on dignity, compassion, and respect.</li>
                <li>Volunteers may interact with individuals and families experiencing hardship.</li>
                <li>All personal information shared by applicants and families must remain confidential.</li>
                <li>Submission of this form does not guarantee placement in a volunteer role.</li>
              </ul>

              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  cursor: "pointer",
                  padding: "14px 16px",
                  backgroundColor: "rgba(245,166,35,0.08)",
                  borderRadius: "10px",
                  border: `1px solid ${form.agreed_to_terms ? GOLD : "rgba(255,255,255,0.15)"}`
                }}
              >
                <input
                  type="checkbox"
                  checked={form.agreed_to_terms}
                  onChange={(e) => update("agreed_to_terms", e.target.checked)}
                  style={{ marginTop: 3, width: 18, height: 18, accentColor: GOLD, cursor: "pointer" }}
                />
                <span style={{ color: "#FFFFFF", fontSize: "15px", fontWeight: "500" }}>I Agree</span>
              </label>
            </div>

            {error && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "14px 18px",
                  backgroundColor: "#FEE2E2",
                  color: "#B91C1C",
                  borderRadius: "10px",
                  marginBottom: "20px",
                  fontSize: "14px"
                }}
              >
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "18px",
                backgroundColor: submitting ? "#94a3b8" : GOLD,
                color: NAVY,
                border: "none",
                borderRadius: "50px",
                fontSize: "15px",
                fontWeight: "700",
                letterSpacing: "0.06em",
                cursor: submitting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                transition: "all 0.2s ease"
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />{" "}
                  SUBMITTING…
                </>
              ) : (
                "SUBMIT VOLUNTEER APPLICATION"
              )}
            </button>
          </form>
        </div>
      </section>

      <SimpleFooter />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input:focus, textarea:focus, select:focus {
          border-color: ${GOLD} !important;
          box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.15);
        }
      `}</style>
    </div>
  );
};

const CheckGroup = ({ options, values, onToggle }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
    {options.map((opt) => {
      const selected = values.includes(opt);
      return (
        <label key={opt} style={{ ...checkRowStyle, ...(selected ? checkboxSelected : {}) }}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(opt)}
            style={{ width: 16, height: 16, accentColor: GOLD, cursor: "pointer", margin: 0 }}
          />
          {opt}
        </label>
      );
    })}
  </div>
);

const SimpleHeader = () => (
  <header
    style={{
      backgroundColor: "rgba(11, 25, 44, 0.98)",
      padding: "18px 24px",
      position: "sticky",
      top: 0,
      zIndex: 50,
      borderBottom: "1px solid rgba(255,255,255,0.08)"
    }}
  >
    <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Link
        to="/"
        style={{
          color: "#FFFFFF",
          textDecoration: "none",
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "1.5rem",
          fontWeight: "500",
          letterSpacing: "0.02em"
        }}
      >
        The Rich Porch
      </Link>
      <Link
        to="/"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          color: "#F5A623",
          textDecoration: "none",
          fontSize: "13px",
          fontWeight: "600",
          letterSpacing: "0.05em"
        }}
      >
        <ArrowLeft size={14} /> BACK TO HOME
      </Link>
    </div>
  </header>
);

const SimpleFooter = () => (
  <footer style={{ backgroundColor: NAVY, color: "rgba(255,255,255,0.7)", textAlign: "center", padding: "28px 24px", fontSize: "13px", letterSpacing: "0.05em" }}>
    © {new Date().getFullYear()} The Rich Porch — A Miracle on Every Porch.
  </footer>
);

export default VolunteerPage;
